import React, { useState, useEffect } from 'react'
import { Menu, X, User, LogOut, LogIn } from 'lucide-react'
import { Link, NavLink } from 'react-router-dom'
import { logoutFromGoogle } from '../../../lib/api/Oauth-Service'
import { getUserInfo } from '../../../lib/api/Auth-Service'
import { useQuery } from '@tanstack/react-query'

const NewNavbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [isVisible, setIsVisible] = useState(true)
  const [lastScrollY, setLastScrollY] = useState(0)

  const loggedInUser = localStorage.getItem('access_token')
  const { data: userinfo } = useQuery({
    queryKey: ['userinfo'],
    queryFn: () => getUserInfo(),
    enabled: !!loggedInUser,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: false,
  })

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY

      // Show navbar at top of page
      if (currentScrollY < 10) {
        setIsVisible(true)
      }
      // Hide navbar when scrolling down, show when scrolling up
      else if (currentScrollY > lastScrollY && currentScrollY > 100) {
        setIsVisible(false)
      } else if (currentScrollY < lastScrollY) {
        setIsVisible(true)
      }
      setLastScrollY(currentScrollY)
    }

    window.addEventListener('scroll', handleScroll, { passive: true })

    return () => window.removeEventListener('scroll', handleScroll)
  }, [lastScrollY])

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen)
  }

  const navLinks = [
    {
      name: 'Courses',
      link: '/all-courses',
    },
    {
      name: 'My Courses',
      link: '/my-courses',
    },
    {
      name: 'Labs',
      link: '/all-lab-groups',
    },
    {
      name: 'Hackathon',
      link: '/hackathon',
    },
    // {
    //   name: 'Subscription',
    //   link: '/subscription',
    // },
  ]

  const handleLogout = async () => {
    setIsMenuOpen(false)
    try {
      const result = await logoutFromGoogle()
      if (result?.status === 200 || result?.status === 204) {
        localStorage.clear()
        window.location.reload()
      }
    } catch (error) {
      console.error('Error: ', error)
    }
  }

  return (
    <>
      {/* Navbar */}
      <nav
        className={`
           fixed top-0 left-0 right-0 z-50 transition-all duration-500 ease-in-out transform ${
             isVisible ? 'translate-y-0 bg-opacity-100' : '-translate-y-full bg-opacity-0'
           }`}>
        <div className="backdrop-blur-md bg-white bg-opacity-20 border-b border-white border-opacity-20 shadow-lg">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
              {/* Logo */}

              <Link to="/" className="no-underline cursor-pointer w-fit flex-shrink-0">
                <img
                  src={
                    'https://s3.brilliant.com.bd/blog-bucket/thumbnail/8c5225dc-da97-48ab-9736-37d815e14439.png' ||
                    'PoridhiLogo.png'
                  }
                  alt="poridhi"
                  className="w-24 sm:w-28 md:w-32 lg:w-44"
                />
              </Link>

              {/* Desktop Navigation */}
              <div className="hidden lg:block">
                <div className="ml-10 flex items-baseline space-x-8">
                  {navLinks.map((link, index) => (
                    <NavLink
                      key={index}
                      to={link.link}
                      className={({ isActive }) =>
                        `font-hindi not-italic font-semibold text-gray-700 hover:text-blue-600 px-3 py-2 rounded-md transition-all duration-200 ${
                          isActive ? 'text-primary font-bold' : 'text-gray-700'
                        }`
                      }>
                      {link.name}
                    </NavLink>
                  ))}
                </div>
              </div>

              {/* Profile Button */}
              {/* <div className="hidden md:block">
                <button className="flex items-center space-x-2 px-4 py-2 rounded-full border-2 border-gray-300 border-opacity-50 bg-white bg-opacity-10 backdrop-blur-sm hover:bg-opacity-20 transition-all duration-200 hover:border-blue-400">
                  <User className="w-5 h-5 text-gray-700" />
                  <span className="text-sm font-medium text-gray-700">
                    {userinfo?.data?.username || userinfo?.data?.email}
                  </span>
                </button>
              </div> */}

              <div className="hidden lg:block">
                {loggedInUser ? (
                  <div className="flex items-center gap-2">
                    <NavLink
                      to={'/student/profile'}
                      className="flex items-center space-x-2 px-4 py-2 rounded-full border-2 border-gray-300 border-opacity-50 bg-white bg-opacity-10 backdrop-blur-sm hover:bg-opacity-20 transition-all duration-200 hover:border-blue-400">
                      <User className="w-5 h-5 text-gray-700" />
                      <span className="text-sm font-medium text-gray-700">
                        {userinfo?.data?.username || userinfo?.data?.email}
                      </span>
                    </NavLink>
                    <button
                      data-tip="Logout"
                      className="flex items-center space-x-2 p-2 rounded-full border-2 border-red border-opacity-50 bg-red bg-opacity-20 backdrop-blur-sm hover:bg-opacity-30 hover:border-red text-red hover:text-red transition-all duration-200 tooltip tooltip-bottom tooltip-error"
                      onClick={handleLogout}>
                      <LogOut className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <NavLink
                    to="/login"
                    className="font-ibm not-italic font-semibold md:font-bold text-sm md:text-base text-white px-6 py-3 bg-primary rounded-lg w-fit text-nowrap">
                    Log In
                  </NavLink>
                )}
              </div>

              {/* Mobile menu button */}
              <div className="lg:hidden">
                <button
                  onClick={toggleMenu}
                  className="p-2 rounded-md text-gray-700 hover:text-blue-600 hover:bg-white hover:bg-opacity-20 transition-all duration-200">
                  {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                </button>
              </div>
            </div>
          </div>

          {/* Mobile Navigation Menu */}
          <div
            className={`lg:hidden transition-all duration-300 ease-in-out overflow-hidden ${
              isMenuOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
            }`}>
            <div className="px-2 pt-2 pb-3 space-y-1 bg-white/10 bg-opacity-10 backdrop-blur-lg border-t border-white border-opacity-20">
              {navLinks.map((link, index) => (
                <NavLink
                  key={index}
                  to={link.link}
                  className={({ isActive }) =>
                    `text-gray-700 hover:text-blue block px-3 py-2 rounded-md text-base font-medium transition-all duration-200 hover:bg-blue/20 hover:bg-opacity-20 ${
                      isActive ? 'text-primary font-bold' : 'text-gray-700'
                    }`
                  }
                  onClick={() => setIsMenuOpen(false)}>
                  {link.name}
                </NavLink>
              ))}

              {/* Mobile Profile */}
              {loggedInUser ? (
                <div className="pt-2 border-t border-white border-opacity-20">
                  <NavLink
                    to="/student/profile"
                    onClick={() => setIsMenuOpen(false)}
                    className="flex items-center space-x-2 w-full px-3 py-2 rounded-md text-blue hover:bg-blue hover:bg-opacity-20 transition-all duration-200">
                    <User className="w-5 h-5" />
                    <span className="text-base font-medium">
                      {userinfo?.data?.username || userinfo?.data?.email}
                    </span>
                  </NavLink>
                  <button
                    className="flex items-center space-x-2 w-full px-3 py-2 rounded-md text-red hover:text-red hover:bg-red hover:bg-opacity-30 transition-all duration-200"
                    onClick={handleLogout}>
                    <LogOut className="w-5 h-5" />
                    <span className="text-base font-medium">Logout</span>
                  </button>
                </div>
              ) : (
                <NavLink
                  onClick={() => setIsMenuOpen(false)}
                  to={'/login'}
                  className="hover:text-blue-600 px-3 py-2 rounded-md text-base font-medium transition-all duration-200 text-primary flex items-center gap-2 hover:bg-primary/10">
                  <LogIn className="size-5" />
                  <span> Log In</span>
                </NavLink>
              )}
            </div>
          </div>
        </div>
      </nav>
    </>
  )
}

export default NewNavbar
